import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  Link,
  useNavigate,
  useParams
} from 'react-router-dom';

import './AdminEventDetail.scss';

const EMPTY_SLOT = {
  starts_at: '',
  ends_at: '',
  capacity: 4
};

const toLocalDateTimeInput = (
  value
) => {
  if (!value) {
    return '';
  }

  const date =
    new Date(value);

  const offset =
    date.getTimezoneOffset();

  const localDate =
    new Date(
      date.getTime() -
        offset * 60000
    );

  return localDate
    .toISOString()
    .slice(0, 16);
};

export default function AdminEventDetail() {
  const {
    id
  } = useParams();

  const navigate =
    useNavigate();

  const apiBaseUrl =
    useMemo(() => {
      const envUrl =
        process.env
          .REACT_APP_API_URL ||
        '';

      return envUrl.endsWith('/')
        ? envUrl.slice(
            0,
            -1
          )
        : envUrl;
    }, []);

  const token =
    localStorage.getItem(
      'tg_admin_token'
    );

  const [
    event,
    setEvent
  ] = useState(null);

  const [
    slots,
    setSlots
  ] = useState([]);

  const [
    attendees,
    setAttendees
  ] = useState([]);

  const [
    activeTab,
    setActiveTab
  ] = useState(
    'overview'
  );

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    busy,
    setBusy
  ] = useState(false);

  const [
    error,
    setError
  ] = useState('');

  const [
    success,
    setSuccess
  ] = useState('');

  const [
    slotForm,
    setSlotForm
  ] = useState(
    EMPTY_SLOT
  );

  const [
    editingSlotId,
    setEditingSlotId
  ] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | Load Event
  |--------------------------------------------------------------------------
  */

  const fetchEvent =
    useCallback(
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await fetch(
              `${apiBaseUrl}/admin/events/${id}`,
              {
                headers: {
                  Authorization:
                    `Bearer ${token}`
                }
              }
            );

          const data =
            await response.json();

          if (!response.ok) {
            throw new Error(
              data?.message ||
                'Unable to load event.'
            );
          }

          setEvent(
            data.event
          );

          setSlots(
            data.slots || []
          );

          setAttendees(
            data.attendees || []
          );
        } catch (err) {
          setError(
            err.message ||
              'Unable to load event.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        apiBaseUrl,
        id,
        token
      ]
    );

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  /*
  |--------------------------------------------------------------------------
  | Formatting
  |--------------------------------------------------------------------------
  */

  const formatDateTime = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    return new Date(
      value
    ).toLocaleString(
      undefined,
      {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }
    );
  };

  const formatTime = (
    value
  ) => {
    if (!value) {
      return '—';
    }

    return new Date(
      value
    ).toLocaleTimeString(
      undefined,
      {
        hour: 'numeric',
        minute: '2-digit'
      }
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Metrics
  |--------------------------------------------------------------------------
  */

  const totalCapacity =
    event?.has_slots
      ? slots.reduce(
          (
            total,
            slot
          ) =>
            total +
            Number(
              slot.capacity ||
                0
            ),
          0
        )
      : Number(
          event?.capacity ||
            0
        );

  const spotsRemaining =
    totalCapacity > 0
      ? Math.max(
          totalCapacity -
            attendees.length,
          0
        )
      : null;

  const registrationPercent =
    totalCapacity > 0
      ? Math.min(
          Math.round(
            (
              attendees.length /
              totalCapacity
            ) *
              100
          ),
          100
        )
      : 0;

  /*
  |--------------------------------------------------------------------------
  | Slot Form
  |--------------------------------------------------------------------------
  */

  const handleSlotChange = (
    event
  ) => {
    const {
      name,
      value
    } = event.target;

    setSlotForm(
      (previous) => ({
        ...previous,
        [name]: value
      })
    );
  };

  const resetSlotForm =
    () => {
      setSlotForm(
        EMPTY_SLOT
      );

      setEditingSlotId(
        null
      );
    };

  const beginEditSlot = (
    slot
  ) => {
    setEditingSlotId(
      slot.slot_id
    );

    setSlotForm({
      starts_at:
        toLocalDateTimeInput(
          slot.starts_at
        ),

      ends_at:
        toLocalDateTimeInput(
          slot.ends_at
        ),

      capacity:
        slot.capacity
    });
  };

  const saveSlot =
    async () => {
      if (
        !slotForm.starts_at
      ) {
        setError(
          'Slot start time is required.'
        );

        return;
      }

      if (
        Number(
          slotForm.capacity
        ) < 1
      ) {
        setError(
          'Slot capacity must be at least 1.'
        );

        return;
      }

      setBusy(true);
      setError('');
      setSuccess('');

      try {
        const payload = {
          starts_at:
            new Date(
              slotForm.starts_at
            ).toISOString(),

          ends_at:
            slotForm.ends_at
              ? new Date(
                  slotForm.ends_at
                ).toISOString()
              : null,

          capacity:
            Number(
              slotForm.capacity
            )
        };

        const url =
          editingSlotId
            ? `${apiBaseUrl}/admin/events/${id}/slots/${editingSlotId}`
            : `${apiBaseUrl}/admin/events/${id}/slots`;

        const method =
          editingSlotId
            ? 'PATCH'
            : 'POST';

        const response =
          await fetch(
            url,
            {
              method,

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  `Bearer ${token}`
              },

              body:
                JSON.stringify(
                  payload
                )
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to save slot.'
          );
        }

        resetSlotForm();

        setSuccess(
          editingSlotId
            ? 'Slot updated successfully.'
            : 'Slot created successfully.'
        );

        await fetchEvent();
      } catch (err) {
        setError(
          err.message ||
            'Unable to save slot.'
        );
      } finally {
        setBusy(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Delete Slot
  |--------------------------------------------------------------------------
  */

  const deleteSlot =
    async (slot) => {
      const confirmed =
        window.confirm(
          `Delete the ${formatTime(
            slot.starts_at
          )} slot?`
        );

      if (!confirmed) {
        return;
      }

      setBusy(true);
      setError('');
      setSuccess('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/events/${id}/slots/${slot.slot_id}`,
            {
              method: 'DELETE',

              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to delete slot.'
          );
        }

        setSuccess(
          'Slot deleted successfully.'
        );

        await fetchEvent();
      } catch (err) {
        setError(
          err.message ||
            'Unable to delete slot.'
        );
      } finally {
        setBusy(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Remove Attendee
  |--------------------------------------------------------------------------
  */

  const removeAttendee =
    async (attendee) => {
      const name =
        `${attendee.first_name || ''} ${attendee.last_name || ''}`
          .trim();

      const confirmed =
        window.confirm(
          `Remove ${
            name ||
            'this attendee'
          } from the event?`
        );

      if (!confirmed) {
        return;
      }

      setBusy(true);
      setError('');
      setSuccess('');

      try {
        const url =
          event.has_slots
            ? `${apiBaseUrl}/admin/events/${id}/slots/${attendee.slot_id}/bookings/${attendee.user_id}`
            : `${apiBaseUrl}/admin/events/${id}/rsvps/${attendee.user_id}`;

        const response =
          await fetch(
            url,
            {
              method: 'DELETE',

              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to remove attendee.'
          );
        }

        setSuccess(
          'Attendee removed successfully.'
        );

        await fetchEvent();
      } catch (err) {
        setError(
          err.message ||
            'Unable to remove attendee.'
        );
      } finally {
        setBusy(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Duplicate Event
  |--------------------------------------------------------------------------
  */

  const duplicateEvent =
    async () => {
      const confirmed =
        window.confirm(
          `Create a copy of "${event.title}"?`
        );

      if (!confirmed) {
        return;
      }

      setBusy(true);
      setError('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/events/${id}/duplicate`,
            {
              method: 'POST',

              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to duplicate event.'
          );
        }

        navigate(
          `/admin/events/${data.event.event_id}/edit`
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to duplicate event.'
        );
      } finally {
        setBusy(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Cancel Event
  |--------------------------------------------------------------------------
  */

  const cancelEvent =
    async () => {
      const confirmed =
        window.confirm(
          `Cancel "${event.title}"?`
        );

      if (!confirmed) {
        return;
      }

      setBusy(true);
      setError('');
      setSuccess('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/events/${id}/cancel`,
            {
              method: 'PATCH',

              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to cancel event.'
          );
        }

        setEvent(
          data.event
        );

        setSuccess(
          'Event cancelled successfully.'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to cancel event.'
        );
      } finally {
        setBusy(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Delete Event
  |--------------------------------------------------------------------------
  */

  const deleteEvent =
    async () => {
      const confirmed =
        window.confirm(
          `Permanently delete "${event.title}"?`
        );

      if (!confirmed) {
        return;
      }

      setBusy(true);
      setError('');

      try {
        const response =
          await fetch(
            `${apiBaseUrl}/admin/events/${id}`,
            {
              method: 'DELETE',

              headers: {
                Authorization:
                  `Bearer ${token}`
              }
            }
          );

        const data =
          await response.json();

        if (!response.ok) {
          throw new Error(
            data?.message ||
              'Unable to delete event.'
          );
        }

        navigate(
          '/admin/events'
        );
      } catch (err) {
        setError(
          err.message ||
            'Unable to delete event.'
        );
      } finally {
        setBusy(false);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Loading
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="admin-event-detail-loading">
        Loading event...
      </div>
    );
  }

  if (!event) {
    return (
      <div className="admin-event-detail-loading">
        Event not found.
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (
    <div className="admin-event-detail">

      {/* --------------------------------------------------
          Heading
      -------------------------------------------------- */}

      <section className="admin-event-detail__heading">
        <div>
          <p className="admin-event-detail__eyebrow">
            Event Management
          </p>

          <h2>
            {event.title}
          </h2>

          <p>
            {formatDateTime(
              event.datetime
            )}

            {event.venue
              ? ` · ${event.venue}`
              : ''}
          </p>
        </div>

        <div className="admin-event-detail__actions">

          <Link
            to={`/admin/events/${id}/edit`}
            className="admin-event-detail__button admin-event-detail__button--primary"
          >
            Edit Event
          </Link>

          <button
            type="button"
            className="admin-event-detail__button"
            onClick={
              duplicateEvent
            }
            disabled={
              busy
            }
          >
            Duplicate
          </button>

          {event.status !==
          'CANCELLED' ? (
            <button
              type="button"
              className="admin-event-detail__button admin-event-detail__button--warning"
              onClick={
                cancelEvent
              }
              disabled={
                busy
              }
            >
              Cancel Event
            </button>
          ) : null}
        </div>
      </section>

      {/* --------------------------------------------------
          Messages
      -------------------------------------------------- */}

      {error ? (
        <div className="admin-event-detail__error">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="admin-event-detail__success">
          {success}
        </div>
      ) : null}

      {event.has_slots &&
      slots.length === 0 ? (
        <div className="admin-event-detail__warning">
          <strong>
            Slot configuration required
          </strong>

          <span>
            This is a slot-based event,
            but no booking slots have
            been created yet. Members
            will not have a slot to
            select.
          </span>

          <button
            type="button"
            onClick={() =>
              setActiveTab(
                'slots'
              )
            }
          >
            Configure Slots
          </button>
        </div>
      ) : null}

      {/* --------------------------------------------------
          Metrics
      -------------------------------------------------- */}

      <section className="admin-event-metrics">

        <div className="admin-event-metric">
          <span>
            Attending
          </span>

          <strong>
            {attendees.length}
          </strong>

          <small>
            registrations
          </small>
        </div>

        <div className="admin-event-metric">
          <span>
            Capacity
          </span>

          <strong>
            {totalCapacity ||
              '—'}
          </strong>

          <small>
            available spots
          </small>
        </div>

        <div className="admin-event-metric">
          <span>
            Remaining
          </span>

          <strong>
            {spotsRemaining ??
              '—'}
          </strong>

          <small>
            spots left
          </small>
        </div>

        <div className="admin-event-metric">
          <span>
            Status
          </span>

          <strong className="admin-event-metric__status">
            {event.status
              ?.replaceAll(
                '_',
                ' '
              )}
          </strong>

          <small>
            event status
          </small>
        </div>
      </section>

      {/* --------------------------------------------------
          Registration Progress
      -------------------------------------------------- */}

      {totalCapacity > 0 ? (
        <section className="admin-event-progress">
          <div className="admin-event-progress__top">
            <span>
              Registration
            </span>

            <strong>
              {attendees.length}
              {' / '}
              {totalCapacity}
            </strong>
          </div>

          <div className="admin-event-progress__track">
            <div
              className="admin-event-progress__fill"
              style={{
                width:
                  `${registrationPercent}%`
              }}
            />
          </div>
        </section>
      ) : null}

      {/* --------------------------------------------------
          Tabs
      -------------------------------------------------- */}

      <nav className="admin-event-tabs">

        <button
          type="button"
          className={
            activeTab ===
            'overview'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'overview'
            )
          }
        >
          Overview
        </button>

        <button
          type="button"
          className={
            activeTab ===
            'attendees'
              ? 'is-active'
              : ''
          }
          onClick={() =>
            setActiveTab(
              'attendees'
            )
          }
        >
          Attendees

          <span>
            {attendees.length}
          </span>
        </button>

        {event.has_slots ? (
          <button
            type="button"
            className={
              activeTab ===
              'slots'
                ? 'is-active'
                : ''
            }
            onClick={() =>
              setActiveTab(
                'slots'
              )
            }
          >
            Slots

            <span>
              {slots.length}
            </span>
          </button>
        ) : null}
      </nav>

      {/* --------------------------------------------------
          Overview
      -------------------------------------------------- */}

      {activeTab ===
      'overview' ? (
        <section className="admin-event-detail-card">

          <div className="admin-event-detail-card__header">
            <div>
              <h3>
                Event Details
              </h3>

              <p>
                Member-facing event
                configuration.
              </p>
            </div>
          </div>

          <div className="admin-event-overview-grid">

            <div>
              <span>
                Type
              </span>

              <strong>
                {event.type
                  ?.replaceAll(
                    '_',
                    ' '
                  ) ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                Starts
              </span>

              <strong>
                {formatDateTime(
                  event.datetime
                )}
              </strong>
            </div>

            <div>
              <span>
                Ends
              </span>

              <strong>
                {formatDateTime(
                  event.ends_at
                )}
              </strong>
            </div>

            <div>
              <span>
                Venue
              </span>

              <strong>
                {event.venue ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                City
              </span>

              <strong>
                {event.city ||
                  '—'}
              </strong>
            </div>

            <div>
              <span>
                Registration
              </span>

              <strong>
                {event.has_slots
                  ? 'Slot Based'
                  : 'Standard RSVP'}
              </strong>
            </div>

            <div>
              <span>
                Access
              </span>

              <strong>
                {event.is_member_only
                  ? 'Members Only'
                  : 'Open'}
              </strong>
            </div>

            <div>
              <span>
                Created
              </span>

              <strong>
                {formatDateTime(
                  event.created_at
                )}
              </strong>
            </div>

          </div>
        </section>
      ) : null}

      {/* --------------------------------------------------
          Attendees
      -------------------------------------------------- */}

      {activeTab ===
      'attendees' ? (
        <section className="admin-event-detail-card">

          <div className="admin-event-detail-card__header">
            <div>
              <h3>
                Attendees
              </h3>

              <p>
                Manage current event
                registrations.
              </p>
            </div>

            <strong className="admin-event-detail-card__count">
              {attendees.length}
              {' '}
              confirmed
            </strong>
          </div>

          {attendees.length ===
          0 ? (
            <div className="admin-event-empty-state">

              <strong>
                No registrations yet
              </strong>

              <span>
                Members who register
                will appear here.
              </span>

            </div>
          ) : (
            <div className="admin-event-table-wrapper">

              <table className="admin-event-table">

                <thead>
                  <tr>
                    <th>
                      Member
                    </th>

                    <th>
                      Email
                    </th>

                    {event.has_slots ? (
                      <th>
                        Slot
                      </th>
                    ) : null}

                    <th>
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {attendees.map(
                    (
                      attendee
                    ) => (
                      <tr
                        key={`${attendee.user_id}-${attendee.slot_id || 'rsvp'}`}
                      >
                        <td>
                          <div className="admin-event-member">
                            <div className="admin-event-member__avatar">
                              {String(
                                attendee.first_name ||
                                  '?'
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <strong>
                              {attendee.first_name}
                              {' '}
                              {attendee.last_name}
                            </strong>
                          </div>
                        </td>

                        <td>
                          {
                            attendee.email
                          }
                        </td>

                        {event.has_slots ? (
                          <td>
                            {formatDateTime(
                              attendee.starts_at
                            )}
                          </td>
                        ) : null}

                        <td>
                          <button
                            type="button"
                            className="admin-event-table__remove"
                            onClick={() =>
                              removeAttendee(
                                attendee
                              )
                            }
                            disabled={
                              busy
                            }
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

              </table>
            </div>
          )}

        </section>
      ) : null}

      {/* --------------------------------------------------
          Slots
      -------------------------------------------------- */}

      {activeTab ===
        'slots' &&
      event.has_slots ? (
        <div className="admin-event-slots-layout">

          <section className="admin-event-detail-card">

            <div className="admin-event-detail-card__header">
              <div>
                <h3>
                  Event Slots
                </h3>

                <p>
                  Booking windows and
                  capacity.
                </p>
              </div>
            </div>

            {slots.length ===
            0 ? (
              <div className="admin-event-empty-state">

                <strong>
                  No slots created
                </strong>

                <span>
                  Create the first slot
                  using the form.
                </span>

              </div>
            ) : (
              <div className="admin-event-slot-list">

                {slots.map(
                  (slot) => {
                    const booked =
                      Number(
                        slot.reserved_count ||
                          0
                      );

                    const capacity =
                      Number(
                        slot.capacity ||
                          0
                      );

                    const remaining =
                      Math.max(
                        capacity -
                          booked,
                        0
                      );

                    return (
                      <div
                        className="admin-event-slot"
                        key={
                          slot.slot_id
                        }
                      >
                        <div className="admin-event-slot__time">
                          <strong>
                            {formatTime(
                              slot.starts_at
                            )}
                          </strong>

                          <span>
                            to
                          </span>

                          <strong>
                            {formatTime(
                              slot.ends_at
                            )}
                          </strong>
                        </div>

                        <div className="admin-event-slot__stats">

                          <div>
                            <span>
                              Booked
                            </span>

                            <strong>
                              {booked}
                            </strong>
                          </div>

                          <div>
                            <span>
                              Capacity
                            </span>

                            <strong>
                              {capacity}
                            </strong>
                          </div>

                          <div>
                            <span>
                              Remaining
                            </span>

                            <strong>
                              {remaining}
                            </strong>
                          </div>

                        </div>

                        <div className="admin-event-slot__actions">

                          <button
                            type="button"
                            onClick={() =>
                              beginEditSlot(
                                slot
                              )
                            }
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="is-danger"
                            onClick={() =>
                              deleteSlot(
                                slot
                              )
                            }
                          >
                            Delete
                          </button>

                        </div>
                      </div>
                    );
                  }
                )}

              </div>
            )}

          </section>

          {/* Slot Editor */}

          <section className="admin-event-slot-editor">

            <div className="admin-event-slot-editor__heading">

              <span>
                {editingSlotId
                  ? 'Edit Slot'
                  : 'Add Slot'}
              </span>

              <strong>
                {editingSlotId
                  ? 'Update booking window'
                  : 'Create booking window'}
              </strong>

            </div>

            <div className="admin-event-slot-editor__field">

              <label>
                Starts
              </label>

              <input
                type="datetime-local"
                name="starts_at"
                value={
                  slotForm.starts_at
                }
                onChange={
                  handleSlotChange
                }
              />

            </div>

            <div className="admin-event-slot-editor__field">

              <label>
                Ends
              </label>

              <input
                type="datetime-local"
                name="ends_at"
                value={
                  slotForm.ends_at
                }
                onChange={
                  handleSlotChange
                }
              />

            </div>

            <div className="admin-event-slot-editor__field">

              <label>
                Capacity
              </label>

              <input
                type="number"
                min="1"
                name="capacity"
                value={
                  slotForm.capacity
                }
                onChange={
                  handleSlotChange
                }
              />

            </div>

            <button
              type="button"
              className="admin-event-slot-editor__save"
              onClick={
                saveSlot
              }
              disabled={
                busy
              }
            >
              {busy
                ? 'Saving...'
                : editingSlotId
                ? 'Save Changes'
                : '+ Add Slot'}
            </button>

            {editingSlotId ? (
              <button
                type="button"
                className="admin-event-slot-editor__cancel"
                onClick={
                  resetSlotForm
                }
              >
                Cancel Edit
              </button>
            ) : null}

          </section>

        </div>
      ) : null}

      {/* --------------------------------------------------
          Danger Zone
      -------------------------------------------------- */}

      <section className="admin-event-danger-zone">

        <div>
          <strong>
            Delete Event
          </strong>

          <span>
            Permanently remove this
            event. Events with existing
            registrations should
            normally be cancelled
            instead.
          </span>
        </div>

        <button
          type="button"
          onClick={
            deleteEvent
          }
          disabled={
            busy
          }
        >
          Delete Event
        </button>

      </section>

    </div>
  );
}